import React from "react";
import styles from "./styles.module.scss";
import useCreateCourt from "@/app/components/map_with_points/components/select_points_sheet_widget/slice";
import {TextBold12, TextBold16, ThirtyText} from "@/app/components/texts";
import Button from "@/app/components/button";
import {t} from "i18next";
import {s} from "@/app/utils/s";
import {courtTypeValues, privateCourtTypeValues} from "@/app/api/courts";
import Chip from "@/app/components/chip";
import LazyAssets from "@/app/utils/assets_widget";
import Input from "@/app/components/input";
import classNames from "classnames";
import CityAutocomplete from "@/app/components/city_autocomplete/widget";
import IconButton from "@/app/components/icon_button";

interface Props {
  coords?: { lat: number; lon: number } | undefined;
  onClose: () => void
  setCoords?: (coords: {
    lat: number;
    lon: number;
  }) => void
}

const CreateCourtWidget: React.FC<Props> = ({coords, onClose, setCoords}) => {
  const {
    state,
    selectPoint,
    setType,
    setPlaceCount,
    setRating,
    setTitle,
    setDescription,
    setArtificialLight,
    setLockerRooms,
    setParkingAvailability,
    setBookingRequired,
    setCoachAvailable,
    setSocialVibe,
    setHasCafeOrBar,
    setRentalGearAvailable,
    selectPhoto,
    unselectPhoto,
    cancel,
    addCourtToMap,
    goBack,
    setAddress,
    setWebsite,
    setPrivateType,
    calculateDisabled
  } = useCreateCourt();

  return (
    <div className={styles.container}>
      {(!state.pointSelected && !state.wantToLeave && !state.courtCreated) && <div className={styles.list}>
          <ThirtyText text={t(s.addCourt)}/>
          <IconButton className={styles.close} icon={<LazyAssets.Close/>} onClick={onClose}/>
          <TextBold16 text={t(s.findAPadelPlace)}/>
        {setCoords && <CityAutocomplete onCitySelect={(place) => {
          setCoords({lat: place.lat, lon: place.lng});
          setAddress(place.fullName);
        }}
                                        className={styles.input} value={""}/>}
          <Button text={t(s.confirm)} onClick={() => selectPoint(coords)} isDisabled={coords === undefined}/>
      </div>}
      {(state.pointSelected && !state.wantToLeave && !state.courtCreated) && <div className={styles.list}>
          <ThirtyText text={t(s.addCourt)} className={styles.title}/>
          <IconButton className={styles.close} icon={<LazyAssets.Close/>} onClick={onClose}/>
          <div className={styles.block}>
              <TextBold16 text={t(s.whatTypeOfCourts)}/>
              <div className={styles.chips}>
                {privateCourtTypeValues.map((type) => (
                  <Chip key={type} text={t(type.replaceAll("_", " "))}
                        isActive={state.court.describeThisLocation === type}
                        onClick={() => setPrivateType(type)}/>))}
              </div>
          </div>
          <div className={styles.block}>
              <TextBold16 text={t(s.howWouldYouDescribeThisLocation)}/>
              <div className={styles.chips}>
                {courtTypeValues.map((type) => (
                  <Chip key={type} text={t(type.replaceAll("_", " "))} isActive={state.court.type.includes(type)}
                        onClick={() => setType(type)}/>))}
              </div>
          </div>

          <div className={styles.block}>
              <TextBold16 text={t(s.howManyCourtsThisPlaceOffers)}/>
              <div className={styles.chips}>
                {[1, 2, 3, 4, 5, 6, 7, 8].map((c) => (
                  <Chip key={c} text={c === 8 ? "8+" : c} isActive={state.court.numberOfCourts === c}
                        onClick={() => setPlaceCount(c)}/>))}
              </div>
          </div>
          <div className={styles.block}>
              <div className={styles.inRow}>
                  <ThirtyText text={t(s.galleryCourt)}/>
                  <TextBold12 text={t(s.firstImageWillBeMain)} className={styles.notLessThan}/>
              </div>
              <div className={styles.uploadBlockWrapper}>
                {[1, 2, 3, 4, 5].map((c) => (
                  <div className={styles.uploadBlock}
                       onClick={state.images && state.images[c] ? () => unselectPhoto(c) : () => selectPhoto(c)}
                       key={c}>
                    {state.images && state.images[c]
                      ? <img src={state.images[c]} alt="" className={styles.image}/>
                      : <LazyAssets.Add className={styles.add}/>}
                    {state.images && state.images[c] &&
                        <IconButton classNameForContainer={styles.closeContainer} icon={<LazyAssets.Close/>}
                                    className={styles.close} onClick={() => unselectPhoto(c)}/>}
                  </div>
                ))}
              </div>
          </div>
          <div className={styles.block}>
              <TextBold16 text={t(s.rateCourts)}/>
              <div className={styles.ratings}>
                {[1, 2, 3, 4, 5].map((c) => (
                  <div className={styles.rating} key={c + 5}
                       onClick={() => setRating(c)}>
                    {state.court.avgRating >= c ? <LazyAssets.Star key={c} className={styles.star}/> :
                      <LazyAssets.StarEmpty key={c} className={styles.star}/>}
                  </div>))}
              </div>
          </div>
          <div className={styles.block}>
              <TextBold16 text={t(s.description)}/>
              <Input value={state.court.name} label={t(s.courtOrClubName)} hint={t(s.addNameOfLocation)}
                     onChange={setTitle}/>
              <Input value={state.court.description} label={t(s.yourReview)} hint={t(s.shareYourExperience)}
                     onChange={setDescription} isTextArea/>
              <Input value={state.court.address} label={t(s.location)} hint={t(s.streetAddress)}
                     onChange={setAddress}/>
              <Input value={state.court.websiteLink} label={t(s.locationWebsite)} hint={t(s.enterTheLocationSite)}
                     onChange={setWebsite}/>
          </div>
          <div className={styles.block}>
              <TextBold16 text={t(s.addMoreDescription)}/>
              <div className={styles.chips}>
                  <Chip text={t(s.artificialLight)} isActive={state.court.artificialLight}
                        onClick={() => setArtificialLight(!state.court.artificialLight)}/>
                  <Chip text={t(s.lockerRooms)} isActive={state.court.lockerRooms}
                        onClick={() => setLockerRooms(!state.court.lockerRooms)}/>
                  <Chip text={t(s.parkingAvailability)} isActive={state.court.parkingAvailability}
                        onClick={() => setParkingAvailability(!state.court.parkingAvailability)}/>
                  <Chip text={t(s.bookingRequired)} isActive={state.court.bookingRequired}
                        onClick={() => setBookingRequired(!state.court.bookingRequired)}/>
                  <Chip text={t(s.coachAvailable)} isActive={state.court.coachAvailable}
                        onClick={() => setCoachAvailable(!state.court.coachAvailable)}/>
                  <Chip text={t(s.socialVibe)} isActive={state.court.socialVibe}
                        onClick={() => setSocialVibe(!state.court.socialVibe)}/>
                  <Chip text={t(s.cafeBar)} isActive={state.court.hasCafeOrBar}
                        onClick={() => setHasCafeOrBar(!state.court.hasCafeOrBar)}/>
                  <Chip text={t(s.rentalGear)} isActive={state.court.rentalGearAvailable}
                        onClick={() => setRentalGearAvailable(!state.court.rentalGearAvailable)}/>
              </div>
          </div>
          <Button text={t(s.cancel)} onClick={() => cancel()} isOutline/>
          <Button text={t(s.addCourtToMap)} onClick={() => addCourtToMap(coords)} isLoading={state.loading}
                  isDisabled={calculateDisabled()}/>
      </div>}
      {(state.wantToLeave && !state.courtCreated) && <div className={classNames(styles.list, styles.center)}>
          <ThirtyText text={t(s.areYouSureDontWantToAddTable)}/>
          <TextBold16 text={t(s.moreNewPlacesToPlayMoreCommunity)}/>
          <div className={styles.inRow}>
              <Button text={t(s.continue)} onClick={onClose} isOutline/>
              <Button text={t(s.cancel)} onClick={goBack}/>
          </div>
      </div>}
      {(!state.wantToLeave && state.courtCreated) && <div className={classNames(styles.list, styles.center)}>
          <ThirtyText text={t(s.courtAdded)}/>
          <TextBold16 text={t(s.afterQuickModerationPublished)}/>
          <Button text={t(s.continue)} onClick={onClose}/>
      </div>}
    </div>
  );
};

export default CreateCourtWidget;